import { AppRoute } from 'constants/AppRoute';

import { MUI, ThemeMode } from 'ci-esi-web-shared';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import {
  selectIsPlanPredictEnabled,
  selectIsSqlEditorEnabled,
  userSelectedDatabase,
} from 'app/containers/PrivateRoute/selectors';
import { headerNavClick as trackEvent } from 'common/analytics';
import { DOCUMENTATION } from 'helpers/constants';
import { translations } from 'locales/i18n';
import { log } from 'services/log';
import { LogEvent } from 'services/LogEvent';
import { LogSource } from 'services/LogSource';
import { colors } from 'styles/theme/colors';

import { DownloadShapefile } from './DownloadShapefile';
import { HeaderItem } from './HeaderItem';
import { Help } from './Help';
import { Logo } from './Logo';
import Notifications from './Notifications';
import { PriceFeed } from './PriceFeed';
import { Profile } from './Profile';
import { NON_ROUTE_HEADER_IDS, getHeaderItemsList, nonRouteHeaderIds } from './utils';

export function getActiveMenuItem(pathname: string) {
  if (pathname.includes(AppRoute.PRODUCTION_PREDICTION)) {
    return AppRoute.PRODUCTION_PREDICTION;
  } else if (pathname.includes(AppRoute.WELL_PLANNER)) {
    return AppRoute.WELL_PLANNER;
  } else if (pathname.includes(AppRoute.ECONOMICS)) {
    return AppRoute.ECONOMICS;
  } else if (pathname.includes(AppRoute.DOCUMENTATION)) {
    return NON_ROUTE_HEADER_IDS.HELP;
  } else if (pathname.includes(DOCUMENTATION)) {
    return NON_ROUTE_HEADER_IDS.HELP;
  } else if (pathname.includes(NON_ROUTE_HEADER_IDS.DOWNLOADS)) {
    return NON_ROUTE_HEADER_IDS.DOWNLOADS;
  }

  return AppRoute.ROOT;
}

function HeaderComponent() {
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const currentPath = pathname.split('/')[1];
  const [anchorHelpEl, setAnchorHelpEl] = useState<HTMLDivElement | null>(null);
  const [shapefileAnchorEl, setShapefileAnchorEl] = useState<HTMLDivElement | null>(null);
  const isSqlEditorEnabled = useSelector(selectIsSqlEditorEnabled);
  const isPlanPredictEnabled = useSelector(selectIsPlanPredictEnabled);
  const [activeMenuItem, setActiveMenuItem] = useState(() => getActiveMenuItem(pathname));
  const selectedDatabaseFromRedux = useSelector(userSelectedDatabase);
  const headerItemsList = getHeaderItemsList(selectedDatabaseFromRedux, isPlanPredictEnabled, isSqlEditorEnabled);

  useEffect(() => {
    setActiveMenuItem(getActiveMenuItem(pathname));
  }, [pathname]);

  const changeActiveMenuHandler = (value: string) => setActiveMenuItem(value);

  const handleMenuItemClick = useCallback(
    ({ currentTarget: { id } }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if ([AppRoute.WELL_PLANNER, AppRoute.PRODUCTION_PREDICTION, AppRoute.ECONOMICS].includes(id)) {
        let source = LogSource.UNKNOWN;
        switch (id) {
          case AppRoute.PRODUCTION_PREDICTION:
            source = LogSource.PREDICT_PAGE_TAB;
            break;

          case AppRoute.WELL_PLANNER:
            source = LogSource.PLAN_PAGE_TAB;
            break;

          case AppRoute.ECONOMICS:
            source = LogSource.ECONOMICS_PAGE_TAB;
            break;

          default:
            break;
        }
        log.info({
          event: LogEvent.CLICK,
          source,
        });
      }

      navigate(id);
      trackEvent(id);
    },
    [navigate],
  );

  const handleHelpClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setAnchorHelpEl(currentTarget);
      trackEvent('', t(translations.HELP.TITLE));
    },
    [t],
  );

  const handleHelpClose = useCallback(() => {
    setAnchorHelpEl(null);
  }, []);

  const handleShapefileClick = useCallback(
    ({ currentTarget }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setShapefileAnchorEl(currentTarget);
      trackEvent('', t(translations.DOWNLOAD_SHAPEFILE.TITLE));
    },
    [t],
  );

  const handleShapefileClose = useCallback(() => {
    setShapefileAnchorEl(null);
  }, []);

  return (
    <MUI.Box
      component="header"
      sx={(theme) => ({
        paddingRight: theme.spacing(2),
        backgroundColor: isDarkMode ? colors.baseGrey57 : colors.primaryWhite,
        boxShadow: isDarkMode ? '0 0 0 1px #444, 0 0 0 2px #121212' : 'none',
        marginBottom: currentPath === DOCUMENTATION ? '1px' : '0px',
        position: 'relative',
      })}
    >
      <MUI.Box
        sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', flexShrink: 0, marginRight: '10px' }}
      >
        <Logo />
        <MUI.Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PriceFeed />
          <Notifications />
          <Profile />
        </MUI.Box>
      </MUI.Box>
      <MUI.Box
        sx={{
          height: '24px',
          display: 'flex',
          flexShrink: 0,
          alignItems: 'center',
          marginRight: '10px',
          paddingBottom: '4px',
        }}
      >
        {headerItemsList.map(({ id, label, testId, isDisabled }) => (
          <>
            <HeaderItem
              labelSx={{
                display: 'flex',
                alignItems: 'center',
                textTransform: 'uppercase',
              }}
              id={id}
              disabled={isDisabled}
              onClick={
                NON_ROUTE_HEADER_IDS.DOWNLOADS === id
                  ? handleShapefileClick
                  : NON_ROUTE_HEADER_IDS.HELP === id
                  ? handleHelpClick
                  : handleMenuItemClick
              }
              showDropDownIcon={nonRouteHeaderIds.includes(id)}
              showBorder={
                (Boolean(anchorHelpEl) && NON_ROUTE_HEADER_IDS.HELP === id) ||
                (Boolean(shapefileAnchorEl) && NON_ROUTE_HEADER_IDS.DOWNLOADS === id) ||
                activeMenuItem === id
              }
              label={t(label)}
              testId={testId}
            />
            {NON_ROUTE_HEADER_IDS.DOWNLOADS === id && (
              <DownloadShapefile
                anchorEl={shapefileAnchorEl}
                onClose={handleShapefileClose}
                changeActiveMenuItem={changeActiveMenuHandler}
                currentPath={currentPath}
              />
            )}
            {NON_ROUTE_HEADER_IDS.HELP === id && <Help anchorEl={anchorHelpEl} onClose={handleHelpClose} />}
          </>
        ))}
      </MUI.Box>
    </MUI.Box>
  );
}

export const HeaderLandingPage = HeaderComponent;
